import type { BlogPost } from "../../lib/blog";
import { no, partial, unstated, yes } from "../comparatifs/cells";

export const meilleurSiteSecretSanta: BlogPost = {
  h1: "Les meilleurs sites de Secret Santa (Père Noël secret) pour Noël 2026",
  seoTitle: "Meilleur site Secret Santa 2026 : comparatif des tirages de Père Noël secret · Listou",
  seoDescription:
    "DrawNames, Elfster, Secret Santa Organizer, Mes Envies, Tirokdo, Tilune, PèreNoelSecret et Listou : prix, compte, exclusions, listes d'envies, ce que voit l'organisateur. Le comparatif sourcé.",
  publishedOn: "2026-09-29",
  updatedOn: "2026-09-29",
  checkedOn: "2026-09-29",
  summary:
    "Tous les sites de Secret Santa, ou Père Noël secret, savent tirer au sort sans que personne ne tombe sur soi-même. Ils diffèrent sur ce qui entoure le tirage : les exclusions pour les couples, le compte demandé aux participants, la liste d'envies de la personne tirée, les messages anonymes, et ce que l'organisateur peut voir. La plupart sont gratuits ; PèreNoelSecret réserve plusieurs fonctions à un Premium à 2,99 €. Listou, notre service, relie le tirage à une vraie liste d'envies et évite de retomber sur la même personne d'une année sur l'autre.",
  table: {
    caption: "Huit sites de Secret Santa, critère par critère",
    columns: [
      "Prix",
      "Compte participants",
      "Exclusions",
      "Liste d'envies",
      "Messages anonymes",
      "Tirage caché à l'organisateur",
    ],
    rows: [
      {
        name: "Listou",
        cells: [
          yes("Gratuit"),
          yes("Aucun : un email, ou le lien du groupe"),
          yes("Gommettes : couple, famille, personne précise"),
          yes("Une liste complète, toutes boutiques"),
          no("Non"),
          partial("Replié, visible s'il le veut ; les participants en sont prévenus"),
        ],
      },
      {
        name: "DrawNames",
        cells: [
          yes("Gratuit"),
          yes("Aucune inscription"),
          yes("Oui"),
          yes("Oui"),
          yes("Questions anonymes"),
          unstated("Non décrit sur les pages consultées"),
        ],
      },
      {
        name: "Elfster",
        cells: [
          yes("Gratuit, sans premium"),
          partial("« Aucun compte requis », mais l'aide en demande un pour confirmer"),
          yes("Oui"),
          yes("Oui, liens de n'importe quelle boutique"),
          yes("Oui"),
          partial("L'organisateur peut « Afficher tous les tirages »"),
        ],
      },
      {
        name: "Secret Santa Organizer",
        cells: [
          yes("Gratuit, avec des publicités Google"),
          yes("Aucun compte"),
          partial("À partir de 4 participants, avant le tirage"),
          yes("Oui"),
          yes("Oui"),
          partial("Combinaisons visibles en option sur la page de gestion"),
        ],
      },
      {
        name: "Mes Envies",
        cells: [
          yes("Gratuit, sans publicité"),
          yes("Aucun compte"),
          yes("Oui"),
          unstated("Non décrit pour le tirage"),
          unstated("Non décrit"),
          unstated("Chacun voit son destinataire ; organisateur non décrit"),
        ],
      },
      {
        name: "Tirokdo",
        cells: [
          yes("Gratuit"),
          unstated("Non précisé ; invitation par email"),
          yes("Oui"),
          yes("« Lettre au Père Noël »"),
          yes("Messages secrets"),
          yes("Il suit les ouvertures, pas les résultats"),
        ],
      },
      {
        name: "Tilune",
        cells: [
          yes("Gratuit, sans premium"),
          yes("Aucun (compte pour l'organisateur)"),
          yes("Oui, avant le tirage"),
          yes("Oui, 3 à 5 idées"),
          unstated("Non décrit"),
          unstated("Non décrit"),
        ],
      },
      {
        name: "PèreNoelSecret",
        cells: [
          partial("Gratuit ; Premium 2,99 € par événement"),
          unstated("Non précisé ; lien personnel par email"),
          partial("Premium, d'après la page Exclusions"),
          partial("Premium"),
          unstated("Non décrit"),
          yes("« L'organisateur ne voit jamais les attributions »"),
        ],
      },
    ],
  },
  body: `
## Comment nous avons comparé

Nous avons retenu les outils de tirage au sort les plus visibles en France pour « secret santa en ligne », « père noël secret » et « tirage au sort cadeaux Noël ». Listou est notre service : nous le présentons avec les autres, et chaque information sur les autres vient de leur propre site, listé en bas de page avec la date de consultation. Quand un site ne décrit pas une fonction, nous l'écrivons « non décrit » plutôt que de supposer qu'elle n'existe pas.

Nous avons écarté les générateurs de tirage génériques (gagnants d'un concours, équipes) : ils ne donnent pas à chacun, en privé, le nom de la personne à qui il offre.

## Ce qu'il faut vérifier avant de choisir

- **Les exclusions.** Sans elles, un couple peut se tirer lui-même, ce qui gâche l'intérêt du jeu. Certains outils les demandent à partir de quatre participants, ou les réservent à une offre payante.
- **Le compte des participants.** Dans une famille, il y a toujours quelqu'un qui ne créera pas de compte. Moins on en demande, plus tout le monde participe.
- **La liste d'envies.** Le tirage dit à qui offrir, pas quoi. Une liste reliée au tirage évite la bougie parfumée de dernière minute.
- **Ce que voit l'organisateur.** S'il participe, mieux vaut qu'il ne connaisse pas tout le tirage, ou que chacun sache qu'il peut le voir.
- **Les retardataires.** Quelqu'un rejoint la famille après le tirage : l'outil doit permettre de l'ajouter.

## Listou : le tirage relié à une vraie liste d'envies

Sur **Listou**, vous ajoutez les prénoms, et l'email de ceux qui en ont un. Les participants n'ont pas de compte à créer : ceux qui ont une adresse reçoivent leur tirage par email, les autres ouvrent le lien du groupe et choisissent leur prénom. L'organisateur, lui, confirme son email avec un code, sans mot de passe. Un groupe compte de 3 à 50 personnes.

Pour les exclusions, on colle la même **gommette** de couleur sur un couple ou une famille : ils ne se tirent pas entre eux. On peut aussi empêcher deux gommettes de se tirer, ou exclure une personne précise.

Le tirage mène directement à la liste d'envies de la personne tirée, avec le budget en tête. C'est une liste Listou complète : articles de n'importe quelle boutique, photo et prix récupérés par lien, et un design que chacun choisit pour la sienne (modèle de page, palette, police, stickers de Noël). Ceux qui n'ont pas encore ouvert leur tirage reçoivent un rappel une semaine, puis deux jours avant l'échange.

Quelqu'un a cliqué sur le mauvais prénom ? « Ce n'est pas moi » : s'il avait déjà vu le tirage, Listou l'échange discrètement avec celui d'une personne qui n'a pas encore regardé. L'an prochain, en préparant l'édition suivante depuis le groupe, Listou garde les participants et leurs règles et écarte les tirages de l'année passée.

Ses limites : l'organisateur **peut** voir le tirage, replié sur sa page, et chaque participant en est prévenu. Il n'y a pas de messagerie anonyme entre la personne qui offre et celle qui reçoit. Pour un retardataire, on l'ajoute puis on refait le tirage ; Listou prévient si des participants avaient déjà découvert le leur.

## DrawNames : gratuit, sans inscription, avec une appli

**DrawNames** se présente comme « 100 % gratuit, aucune inscription requise ». Il gère les exclusions, les listes de souhaits et les questions anonymes, et propose une application iPhone et Android en plus du site. Les pages que nous avons consultées ne décrivent pas ce que l'organisateur peut voir du tirage.

## Elfster : le plus complet, mais l'organisateur voit tout

**Elfster** est gratuit, « pour toujours », sans offre premium. Il réunit les exclusions pour les couples, un budget recommandé et une date avec rappels, des listes d'envies où l'on colle un lien d'Amazon, d'Etsy ou d'ailleurs, et des questions ou indices anonymes. Des applis iPhone et Android existent.

Deux points à savoir. Sa page de présentation annonce « aucun compte requis pour participer », mais son centre d'aide indique qu'un invité sans compte Elfster devra en créer un pour confirmer sa participation. Et l'aide précise que les organisateurs et assistants peuvent « Afficher tous les tirages » : le secret ne tient pas vis-à-vis d'eux.

## Secret Santa Organizer : sans compte, avec messages anonymes

**Secret Santa Organizer** est gratuit, sans formule premium, et se finance avec des publicités Google. Aucun compte n'est demandé. Chaque participant reçoit son attribution par email, avec un lien vers sa page, où il remplit sa liste d'envies, lit celle de la personne tirée et peut lui envoyer un message anonyme.

Les exclusions sont possibles à partir de quatre participants, et seulement avant le début du tirage. L'administrateur peut, s'il le souhaite, afficher les combinaisons sur la page de gestion.

## Mes Envies : le tirage le plus simple

Le tirage de **Mes Envies** est gratuit, sans compte, et le site insiste : « pas de suggestions de cadeaux, de publicité, de partenariats ». Il gère des exclusions personnalisées, par exemple pour un couple, et chacun reçoit son destinataire en toute confidentialité.

La page ne décrit ni budget, ni date, ni liste d'envies reliée au tirage, ni messages anonymes. Mes Envies propose par ailleurs des listes de Noël, séparément.

## Tirokdo : trois modes de tirage et un chat secret

**Tirokdo** est gratuit et fonctionne dans le navigateur, sans appli. Chaque participant reçoit une invitation par email vers son espace, où il écrit sa « lettre au Père Noël » et échange des messages secrets avec la personne tirée. L'organisateur fixe la date, les règles, un budget éventuel et des exclusions ; le mode Expert permet jusqu'à cinq cadeaux par personne. Il peut vérifier que chacun a ouvert son tirage, sans en connaître les résultats.

## Tilune : gratuit, jusqu'à 50 participants

Le Secret Santa complet de **Tilune** est gratuit, sans fonction premium, de 3 à 50 participants. L'organisateur crée un compte gratuit ; les participants rejoignent sans inscription obligatoire, par email ou par lien. Chacun reçoit son attribution par email et peut ajouter une liste de 3 à 5 idées. L'organisateur fixe un budget minimum et maximum et une date limite d'inscription, et les exclusions se règlent avant le tirage.

## PèreNoelSecret : l'organisateur ne voit rien

**PèreNoelSecret** est gratuit pour le tirage classique. Sa page d'accueil l'affirme : « l'organisateur ne voit jamais les attributions ». Le Premium, à 2,99 € par événement sans abonnement, ajoute les listes de souhaits, l'ajout de participants après le tirage et le re-tirage de ceux qui n'ont pas encore révélé le leur ; sa page dédiée y range aussi les exclusions. En gratuit, la liste est figée après le tirage. L'envoi par SMS existe en option, avec des packs achetés à part.

## Quel site choisir ?

- **L'organisateur participe et veut être surpris** : PèreNoelSecret, où il ne voit jamais les attributions, ou Tirokdo, où il suit les ouvertures sans les résultats.
- **Vous voulez que chacun sache quoi offrir** : Listou, dont le tirage mène à une liste complète, ou Elfster, DrawNames, Secret Santa Organizer et Tilune, qui ont une liste d'envies.
- **Vous voulez poser des questions à la personne tirée** : Elfster, DrawNames, Secret Santa Organizer ou Tirokdo.
- **Vous refaites le Secret Santa chaque année** : Listou, qui écarte les tirages de l'an dernier.
- **Vous voulez juste tirer au sort, sans rien d'autre** : Mes Envies.

Pour organiser le vôtre, le [Secret Santa de Listou](/secret-santa) se prépare en deux minutes ; les [règles et le budget](/blog/pere-noel-secret-regles-budget) sont détaillés dans notre guide. Et si la famille veut aussi échanger des listes sans tirage, les [listes en groupe](/listes-en-groupe) réunissent celles de tout le monde derrière un seul lien.
`,
  faq: [
    [
      "Quel est le meilleur site de Secret Santa en 2026 ?",
      "Cela dépend de votre groupe. Pour que chacun sache quoi offrir grâce à une vraie liste d'envies, Listou. Pour que l'organisateur ne voie jamais le tirage, PèreNoelSecret. Pour des questions anonymes à la personne tirée, Elfster, DrawNames, Secret Santa Organizer ou Tirokdo. Pour un tirage simple sans rien d'autre, Mes Envies.",
    ],
    [
      "Quel site de Secret Santa est gratuit ?",
      "Listou, DrawNames, Elfster, Secret Santa Organizer, Mes Envies, Tirokdo et Tilune sont gratuits. PèreNoelSecret est gratuit pour le tirage classique, avec un Premium à 2,99 € par événement pour les listes de souhaits et l'ajout de participants après le tirage.",
    ],
    [
      "Comment éviter qu'un couple se tire au sort ?",
      "Utilisez les exclusions. Sur Listou, collez la même gommette au couple. Secret Santa Organizer les accepte à partir de quatre participants ; PèreNoelSecret les place dans son Premium d'après sa page dédiée.",
    ],
    [
      "Les participants doivent-ils créer un compte ?",
      "Non sur Listou, DrawNames, Secret Santa Organizer, Mes Envies et Tilune. Sur Elfster, la page de présentation dit qu'aucun compte n'est requis, mais l'aide demande aux invités sans compte d'en créer un pour confirmer.",
    ],
    [
      "L'organisateur peut-il participer sans connaître le tirage ?",
      "Sur PèreNoelSecret, l'organisateur ne voit jamais les attributions. Sur Tirokdo, il suit les ouvertures sans les résultats. Sur Elfster et Secret Santa Organizer, il peut afficher les tirages. Sur Listou, le tirage est replié sur sa page et les participants sont prévenus qu'il peut le voir.",
    ],
    [
      "Peut-on ajouter quelqu'un après le tirage ?",
      "Sur Listou, ajoutez la personne puis refaites le tirage : Listou prévient si certains avaient déjà découvert le leur. Sur PèreNoelSecret, c'est une fonction Premium ; en gratuit, la liste est figée après le tirage.",
    ],
    [
      "Peut-on éviter de retomber sur la même personne que l'an dernier ?",
      "Sur Listou, oui : en préparant l'édition suivante depuis le groupe de l'année passée, les participants et leurs règles sont repris et les tirages de l'an dernier écartés.",
    ],
  ],
  cta: "secret_santa",
  sources: [
    { label: "DrawNames : tirage au sort", url: "https://www.drawnames.fr/tirage-au-sort" },
    { label: "Elfster : générateur de Secret Santa", url: "https://www.elfster.com/lp/fr/ssg" },
    {
      label: "Elfster : comment participer à un échange de cadeaux",
      url: "https://help.elfster.com/fr/articles/11500547-comment-participer-a-un-echange-de-cadeaux-elfster",
    },
    {
      label: "Elfster : voir qui a tiré au sort quelqu'un ou tout le monde",
      url: "https://help.elfster.com/fr/articles/11500522-comment-voir-qui-a-tire-au-sort-quelqu-un-ou-tout-le-monde",
    },
    { label: "Secret Santa Organizer : FAQ", url: "https://www.secretsantaorganizer.com/fr/faq" },
    { label: "Mes Envies : Secret Santa", url: "https://www.mesenvies.fr/secret-santa.html" },
    { label: "Tirokdo : page d'accueil", url: "https://www.tirokdo.com/" },
    {
      label: "Tirokdo : organiser un Secret Santa",
      url: "https://faq.tirokdo.com/fr/article/comment-organiser-un-secret-santa-avec-tirokdo-c7pbjd/",
    },
    { label: "Tilune : Secret Santa de Noël", url: "https://www.tilune.fr/secret-santa-noel" },
    { label: "Tilune : tirage Secret Santa", url: "https://www.tilune.fr/tirage-secret-santa" },
    { label: "PèreNoelSecret : page d'accueil", url: "https://perenoelsecret.fr/" },
    {
      label: "PèreNoelSecret : exclusions",
      url: "https://perenoelsecret.fr/pere-noel-secret-avec-exclusion",
    },
  ],
};
